/**
 * React Query mutation hook for updating an existing component rule.
 *
 * Not used directly by Phase 5 (the config page only creates and
 * transitions rules at this stage), but exposed via the barrel so
 * follow-up phases can edit rule bodies without re-inventing the cache
 * plumbing.
 */

import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import {
  componentRulesService,
  type ComponentRuleDTO,
  type UpdateComponentRulePayload,
} from '../../api/componentRulesService';
import { componentRulesKeys } from './queryKeys';

/** Variables accepted by `useUpdateComponentRule().mutate(...)`. */
export interface UpdateComponentRuleVariables {
  id: string;
  payload: UpdateComponentRulePayload;
  /**
   * Optional component id hint; when provided, the list query for that
   * component is invalidated alongside the generic `lists()` prefix. The
   * caller usually knows the id because it's the config page they are
   * currently on, so passing it is nearly free.
   */
  componentId?: string;
}

/** Mutation hook for `PUT /claimflow/component-rules/{id}`. */
export function useUpdateComponentRule(): UseMutationResult<
  ComponentRuleDTO,
  Error,
  UpdateComponentRuleVariables
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: UpdateComponentRuleVariables) =>
      componentRulesService.updateRule(id, payload),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: componentRulesKeys.detail(variables.id),
      });
      void queryClient.invalidateQueries({
        queryKey: componentRulesKeys.lists(),
      });
      if (variables.componentId !== undefined) {
        void queryClient.invalidateQueries({
          queryKey: componentRulesKeys.list(variables.componentId),
        });
      }
    },
  });
}
